/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Require Auth0 login before rendering a dashboard route. Shows PageLoader while redirecting.
 */

import { withAuthenticationRequired } from "@auth0/auth0-react";
import React from "react";
import { PageLoader } from "./PageLoader";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../config/routes";
import { logger } from "../utils/logger";

export const AuthenticationGuard = ({ component, onError, skipEmailCheck = false }) => {
  const location = useLocation();
  
  const Component = withAuthenticationRequired(component, {
    onRedirecting: () => (
      <div className="page-layout">
        <PageLoader />
      </div>
    ),
    // Preserve the return URL (including query params) when redirecting to login
    returnTo: () => {
      // Preserve the full path including query parameters (e.g., /setup)
      return location.pathname + location.search;
    },
    // Handle login errors
    onError: (error) => {
      logger.error('AuthenticationGuard error:', error);
      if (onError) {
        onError(error);
      }
      // Default error handling - redirect to home on certain errors
      if (error?.error === 'access_denied' || error?.error === 'consent_required') {
        window.location.href = ROUTES.HOME;
      }
    },
  });

  // Skip email check if explicitly requested (e.g., for email verification page itself)
  // NOTE: With Passwordless Email OTP (hosted login), we do NOT gate the app with a custom email-verify page.
  // Auth0 handles the email ownership via OTP.
  void skipEmailCheck;
  return <Component />;
};
